/**
 * Guardrail Validation System for Ameen Insurance Assistant
 * 
 * Provides pre-built guardrails and validation utilities for
 * controlling agent transitions and preventing invalid flow jumps.
 */

import { AgentContext, Guardrail, EntryGuardrails, SupportedLanguage } from './types';

// =============================================================================
// COMMON GUARDRAILS - Reusable across multiple agents
// =============================================================================

/**
 * Guardrail: Language must be detected
 */
export const languageDetectedGuardrail: Guardrail = {
  id: 'language_detected',
  description: 'User language preference must be set',
  validate: (context: AgentContext) => {
    if (!context.transitionData.language) {
      return 'Language has not been detected yet. Cannot proceed without language preference.';
    }
    return true;
  },
};

/**
 * Guardrail: User data must be available
 */
export const userDataRequiredGuardrail: Guardrail = {
  id: 'user_data_required',
  description: 'User must be logged in with name and mobile number',
  validate: (context: AgentContext) => {
    const userData = context.transitionData.userData;
    if (!userData || !userData.username || !userData.mobileNumber) {
      return 'User data (name and mobile) is required for this operation.';
    }
    return true;
  },
};

/**
 * Guardrail: Voice gender must be set
 */
export const voiceGenderSetGuardrail: Guardrail = {
  id: 'voice_gender_set',
  description: 'Voice gender preference must be configured',
  validate: (context: AgentContext) => {
    if (!context.transitionData.voiceGender) {
      return 'Voice gender has not been set.';
    }
    return true;
  },
};

// =============================================================================
// POLICY FLOW GUARDRAILS
// =============================================================================

/**
 * Guardrail: Package must be selected
 */
export const packageSelectedGuardrail: Guardrail = {
  id: 'package_selected',
  description: 'User must select a package before proceeding',
  validate: (context: AgentContext) => {
    if (!context.transitionData.selected_package) {
      return 'No package has been selected. User must choose a package first.';
    }
    return true;
  },
};

/**
 * Guardrail: Customer data must be collected
 */
export const customerDataRequiredGuardrail: Guardrail = {
  id: 'customer_data_required',
  description: 'Customer identity data must be collected',
  validate: (context: AgentContext) => {
    if (!context.transitionData.customer_data) {
      return 'Customer data has not been collected.';
    }
    return true;
  },
};

/**
 * Guardrail: Survey images must be captured
 */
export const surveyImagesRequiredGuardrail: Guardrail = {
  id: 'survey_images_required',
  description: 'Vehicle survey images must be captured',
  validate: (context: AgentContext) => {
    if (!context.transitionData.survey_images || context.transitionData.survey_images.count === 0) {
      return 'Vehicle survey images have not been captured.';
    }
    return true;
  },
};

// =============================================================================
// CLAIM FLOW GUARDRAILS
// =============================================================================

/**
 * Guardrail: Policy number must be available
 */
export const policyNumberRequiredGuardrail: Guardrail = {
  id: 'policy_number_required',
  description: 'Policy number must be provided for claim',
  validate: (context: AgentContext) => {
    if (!context.transitionData.policy_number) {
      return 'Policy number has not been provided.';
    }
    return true;
  },
};

/**
 * Guardrail: Claim request number must exist
 */
export const claimRequestNumRequiredGuardrail: Guardrail = {
  id: 'claim_request_num_required',
  description: 'Claim request number must be available',
  validate: (context: AgentContext) => {
    if (!context.transitionData.claim_request_num) {
      return 'Claim request number is not available.';
    }
    return true;
  },
};

// =============================================================================
// VEHICLE INSPECTION GUARDRAILS
// =============================================================================

/**
 * Guardrail: Caller agent must be specified for return
 */
export const callerAgentRequiredGuardrail: Guardrail = {
  id: 'caller_agent_required',
  description: 'Caller agent must be specified for vehicle inspection return',
  validate: (context: AgentContext) => {
    if (!context.transitionData.caller_agent) {
      return 'Caller agent is not specified. Cannot return from vehicle inspection.';
    }
    return true;
  },
};

/**
 * Guardrail: Damage description must be available
 */
export const damageDescriptionRequiredGuardrail: Guardrail = {
  id: 'damage_description_required',
  description: 'Vehicle damage description must be available',
  validate: (context: AgentContext) => {
    if (!context.transitionData.damage_description) {
      return 'Vehicle damage description has not been generated.';
    }
    return true;
  },
};

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Validate all required guardrails for entry
 * @returns Array of error messages, empty if all pass
 */
export function validateEntryGuardrails(
  guardrails: EntryGuardrails,
  context: AgentContext
): string[] {
  const errors: string[] = [];
  
  for (const guardrail of guardrails.required) {
    const result = guardrail.validate(context);
    if (result !== true) {
      errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
    }
  }
  
  // Log optional guardrail warnings (but don't block)
  if (guardrails.optional) {
    for (const guardrail of guardrails.optional) {
      const result = guardrail.validate(context);
      if (result !== true) {
        console.warn(`[GUARDRAIL WARNING] ${guardrail.id}: ${result}`);
      }
    }
  }
  
  return errors;
}

/**
 * Find valid transition from exit guardrails
 * @returns Target agent ID and any errors, or null if no valid transition
 */
export function findValidTransition(
  exitGuardrails: Map<string, { target: { agentId: string }; guardrails: Guardrail[] }>,
  transitionName: string,
  context: AgentContext
): { targetAgentId: string; errors: string[] } | null {
  const transition = exitGuardrails.get(transitionName);
  
  if (!transition) {
    return null;
  }
  
  const errors: string[] = [];
  for (const guardrail of transition.guardrails) {
    const result = guardrail.validate(context);
    if (result !== true) {
      errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
    }
  }
  
  return {
    targetAgentId: transition.target.agentId,
    errors,
  };
}

/**
 * Create a simple "no requirements" entry guardrails object
 */
export function noEntryRequirements(): EntryGuardrails {
  return {
    required: [],
    optional: [],
  };
}

/**
 * Create entry guardrails with just language requirement
 */
export function requireLanguage(): EntryGuardrails {
  return {
    required: [languageDetectedGuardrail],
    optional: [],
  };
}

/**
 * Create entry guardrails requiring language and user data
 */
export function requireLanguageAndUser(): EntryGuardrails {
  return {
    required: [languageDetectedGuardrail, userDataRequiredGuardrail],
    optional: [],
  };
}

/**
 * Create a custom guardrail
 */
export function createGuardrail(
  id: string,
  description: string,
  validate: (context: AgentContext) => boolean | string
): Guardrail {
  return { id, description, validate };
}

/**
 * Combine multiple guardrails into an AND condition
 */
export function allOf(...guardrails: Guardrail[]): Guardrail {
  return {
    id: `all_of_${guardrails.map(g => g.id).join('_')}`,
    description: `All of: ${guardrails.map(g => g.description).join(', ')}`,
    validate: (context: AgentContext) => {
      for (const guardrail of guardrails) {
        const result = guardrail.validate(context);
        if (result !== true) {
          return result;
        }
      }
      return true;
    },
  };
}

/**
 * Combine multiple guardrails into an OR condition
 */
export function anyOf(...guardrails: Guardrail[]): Guardrail {
  return {
    id: `any_of_${guardrails.map(g => g.id).join('_')}`,
    description: `Any of: ${guardrails.map(g => g.description).join(', ')}`,
    validate: (context: AgentContext) => {
      const errors: string[] = [];
      for (const guardrail of guardrails) {
        const result = guardrail.validate(context);
        if (result === true) {
          return true;
        }
        errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
      }
      return errors.join(' OR ');
    },
  };
}
